"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Modal } from "react-bootstrap"
import { useAuth0 } from "@auth0/auth0-react"
import { Link } from "react-router-dom"
import "../../styles/components/CommunityForum.css"

export default function FriendsModal() {
  const [showFriends, setShowFriends] = useState(false)
  const [friends, setFriends] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0()

  const removeSpecialCharacters = (value) => value.replace(/[-|]/g, "")

  useEffect(() => {
    const fetchFriends = async () => {
      if (!showFriends || !isAuthenticated || isLoading) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        })

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/friends/getfriends`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error("Failed to fetch friends")
        }

        const data = await response.json()
        const cleanUserSub = removeSpecialCharacters(user.sub)
        const friendUserIds = data
          .map((friend) => {
            const cleanRequesterId = removeSpecialCharacters(friend.requesterId)
            const cleanRecipientId = removeSpecialCharacters(friend.recipientId)
            if (cleanRequesterId === cleanUserSub) {
              return friend.recipientId
            }
            if (cleanRecipientId === cleanUserSub) {
              return friend.requesterId
            }
            return null
          })
          .filter(Boolean)

        const uniqueFriendIds = [...new Set(friendUserIds)]

        if (uniqueFriendIds.length === 0) {
          setFriends([])
          return
        }

        const profilesResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/getusersbyids`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(uniqueFriendIds),
        })

        if (!profilesResponse.ok) {
          throw new Error("Failed to fetch friend profiles")
        }

        const profiles = await profilesResponse.json()
        const normalized = profiles
          .map((profile) => {
            const name = [profile.userFname, profile.userLname].filter(Boolean).join(" ")
            return {
              id: profile.auth0UserId || profile.userId,
              name: name || "Friend",
              email: profile.userEmail || "Email unavailable",
              bio: profile.userBio || "No bio provided yet.",
              avatar: profile.profilePictureUrl,
            }
          })
          .sort((a, b) => a.name.localeCompare(b.name))

        setFriends(normalized)
      } catch (fetchError) {
        console.error("Error fetching friends:", fetchError)
        setError("We couldn't load your friends right now. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchFriends()
  }, [getAccessTokenSilently, isAuthenticated, isLoading, showFriends, user])

  const hasFriends = useMemo(() => friends.length > 0, [friends])

  const handleKeyPress = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      setShowFriends(true)
    }
  }

  return (
    <>
      <div className="friends-banner">
        <div className="friends-banner-copy">
          <p className="friends-banner-pill">Connections</p>
          <h3 className="friends-banner-title">Stay close to your Kingdom circle</h3>
          <p className="friends-banner-subtitle">
            Keep track of the people you&apos;re building with and jump into their latest updates.
          </p>
        </div>
        <div className="friends-banner-actions">
          <Link className="friends-banner-search" to="/user-search">
            Friend Search
          </Link>
          <button
            type="button"
            className="friends-banner-button"
            onClick={() => setShowFriends(true)}
            onKeyDown={handleKeyPress}
          >
            My Friends
          </button>
        </div>
      </div>

      <Modal
        show={showFriends}
        onHide={() => setShowFriends(false)}
        centered
        dialogClassName="friends-modal"
        contentClassName="friends-modal-content"
        onClick={() => setShowFriends(false)}
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title className="friends-modal-title">My friends</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="friends-modal-intro">Here are the friends you&apos;re connected with in the Kingdom.</p>
          {error && <div className="friends-modal-state">{error}</div>}
          {loading && <div className="friends-modal-state">Loading friends...</div>}
          {!loading && !error && !hasFriends && (
            <div className="friends-modal-state">No friends yet. Add a few connections to see them here.</div>
          )}
          {!loading && !error && hasFriends && (
            <div className="friends-modal-grid">
              {friends.map((friend) => (
                <article key={friend.id} className="friends-card">
                  {friend.avatar ? (
                    <img src={friend.avatar} alt={`${friend.name} profile`} className="friends-avatar" />
                  ) : (
                    <div className="friends-avatar friends-avatar-fallback">{friend.name.charAt(0)}</div>
                  )}
                  <div className="friends-card-body">
                    <h4 className="friends-card-name">{friend.name}</h4>
                    <p className="friends-card-email">{friend.email}</p>
                    <p className="friends-card-bio">{friend.bio}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}
